import { InstrumentCategoryEnum, ListedInstrument } from '@eih-netbroker/common';
import { useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-line.svg';
import { ReactComponent as KIDIcon } from '../../../assets/svg/kid.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { EmptyModalContent } from '../../../shared/components/empty-modal-content/EmptyModalContent';
import { NbIconButton } from '../../../shared/components/icon-button/IconButton';
import { NbLoader } from '../../../shared/components/loader/Loader';
import { NbTooltip } from '../../../shared/components/tooltip/Tooltip';
import { useProductDetails } from '../services/product/useProductDetails';
import { useProductDetailsListsForCertificate } from '../services/product/useProductDetailsListsForCertificate';
import { useProductDetailsListForCurrency } from '../services/product/useProductDetailsListsForCurrency';
import { useProductDetailsListsForETF } from '../services/product/useProductDetailsListsForETF';
import { useProductDetailsListsForFuture } from '../services/product/useProductDetailsListsForFuture';
import { useProductDetailsListsForIndex } from '../services/product/useProductDetailsListsForIndex';
import { useProductDetailsListsForInvestmentUnits } from '../services/product/useProductDetailsListsForInvestmentUnits';
import { useProductDetailsListForShare } from '../services/product/useProductDetailsListsForShare';
import classes from './ProductDetailsModal.module.scss';
import { WidgetInstrumentSearchAndDisplay } from './WidgetInstrumentSearchAndDisplay';
import { ProductDetailsHeader } from './product/ProductDetailsHeader';
import { ProductDetailsSection } from './product/ProductDetailsSection';

export interface ProductDetailsModalProps {
  onCancel: () => void;
  instrument: ListedInstrument;
}

export const ProductDetailsModal = ({ onCancel, instrument: initialInstrument }: ProductDetailsModalProps) => {
  const { translation } = useLocale();
  const [instrument, setInstrument] = useState<ListedInstrument | null>(initialInstrument || null);
  const source = `product-details-${instrument?.instrumentName}`;
  const { product } = useProductDetails(instrument, source);
  const { tradingFieldsForShare, fundamentalFieldsForShare, instrumentFieldsForShare } = useProductDetailsListForShare(
    instrument,
    3,
  );
  const { tradingFieldsForCurrency, instrumentFieldsForCurrency } = useProductDetailsListForCurrency(instrument, 3);
  const { tradingFieldsForFuture, instrumentFieldsForFuture } = useProductDetailsListsForFuture(instrument, 3);
  const { tradingFieldsForIndex, instrumentFieldsForIndex } = useProductDetailsListsForIndex(instrument, 3);
  const { tradingFieldsForInvestmentUnits, instrumentFieldsForInvestmentUnits } =
    useProductDetailsListsForInvestmentUnits(instrument, 3);
  const { tradingFieldsForCertificate, instrumentFieldsForCertificate } = useProductDetailsListsForCertificate(
    instrument,
    3,
  );
  const { tradingFieldsForETF, instrumentFieldsForETF } = useProductDetailsListsForETF(instrument, 3);

  const selectInstrument = (instrument: ListedInstrument | null) => {
    setInstrument(instrument);
  };

  return (
    <div className={classes.product}>
      <div className={classes.productHeader}>
        <h2 className={classes.productTitle}>{translation.productDetailsModal.title}</h2>
        <NbIconButton
          onClick={onCancel}
          icon={<CloseIcon />}
        />
      </div>
      <div className={classes.productSearchContainer}>
        <WidgetInstrumentSearchAndDisplay
          className={classes.searchInput}
          instrument={instrument}
          updateInstrument={selectInstrument}
          disableSubscription={true}
          warningTooltip={translation.productDetails.noEstreamConnection}
        />
      </div>
      {product.hasError && <EmptyModalContent title={translation.productDetails.cannotBeLoaded} />}
      {instrument && product.isLoading && (
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      )}
      {instrument && product.isLoaded && (
        <>
          <ProductDetailsHeader
            className={classes.containerHeader}
            instrument={instrument}
            product={product}
          />
          <div className={classes.sectionsContainer}>
            {instrument.instrumentCategory === InstrumentCategoryEnum.Equity && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForShare[0]}
                  list2={tradingFieldsForShare[1]}
                  list3={tradingFieldsForShare[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.fundamentalIndicators}
                  list1={fundamentalFieldsForShare[0]}
                  list2={fundamentalFieldsForShare[1]}
                  list3={fundamentalFieldsForShare[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForShare[0]}
                  list2={instrumentFieldsForShare[1]}
                  list3={instrumentFieldsForShare[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.Currency && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForCurrency[0]}
                  list2={tradingFieldsForCurrency[1]}
                  list3={tradingFieldsForCurrency[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForCurrency[0]}
                  list2={instrumentFieldsForCurrency[1]}
                  list3={instrumentFieldsForCurrency[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.Futures && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForFuture[0]}
                  list2={tradingFieldsForFuture[1]}
                  list3={tradingFieldsForFuture[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForFuture[0]}
                  list2={instrumentFieldsForFuture[1]}
                  list3={instrumentFieldsForFuture[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.Index && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForIndex[0]}
                  list2={tradingFieldsForIndex[1]}
                  list3={tradingFieldsForIndex[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForIndex[0]}
                  list2={instrumentFieldsForIndex[1]}
                  list3={instrumentFieldsForIndex[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.InvestmentFund && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForInvestmentUnits[0]}
                  list2={tradingFieldsForInvestmentUnits[1]}
                  list3={tradingFieldsForInvestmentUnits[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForInvestmentUnits[0]}
                  list2={instrumentFieldsForInvestmentUnits[1]}
                  list3={instrumentFieldsForInvestmentUnits[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.Certificate && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForCertificate[0]}
                  list2={tradingFieldsForCertificate[1]}
                  list3={tradingFieldsForCertificate[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForCertificate[0]}
                  list2={instrumentFieldsForCertificate[1]}
                  list3={instrumentFieldsForCertificate[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            {instrument.instrumentCategory === InstrumentCategoryEnum.ETF && (
              <div>
                <ProductDetailsSection
                  title={translation.productDetails.tradingData}
                  list1={tradingFieldsForETF[0]}
                  list2={tradingFieldsForETF[1]}
                  list3={tradingFieldsForETF[2]}
                  columnSize={3}
                />
                <ProductDetailsSection
                  title={translation.productDetails.instrumentData}
                  list1={instrumentFieldsForETF[0]}
                  list2={instrumentFieldsForETF[1]}
                  list3={instrumentFieldsForETF[2]}
                  columnSize={3}
                  shouldNotCapValueLength
                />
              </div>
            )}

            <div className={classes.kid}>
              <NbTooltip
                title={translation.general.KIDTooltip}
                placement={'top'}
              >
                <a
                  href="https://kid.ersteinvestment.hu/"
                  target={'_blank'}
                  rel="noopener noreferrer"
                >
                  {translation.general.KIDDocumentSearch} <KIDIcon />
                </a>
              </NbTooltip>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
